import {
  InvestorDetailsGetInvestorLegalEntitiesQuery,
  InvestorSortBy,
  InvestorSortDirection,
  TaxStatus,
  Category as gqlCategory,
  Eligibility as gqlEligibility,
  EntityType as gqlEntityType
} from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import CheckBox from '@/components/basicComponents/checkbox';
import DatePicker from '@/components/basicComponents/datePicker';
import Input from '@/components/basicComponents/input';
import Label from '@/components/basicComponents/label';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Textarea from '@/components/basicComponents/textarea';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { Category, Eligibility, EntityType, TenantType } from '@trace-8/trace8-schema';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '../../queries';
import { RadioButtonWithNotification } from '../components';
import AddInvestorHousehold from '../components/modals/addInvestorHousehold';
import { editInvestorAddInvestorEntityInvestorHouseholdOptions, editInvestorEntityDetailsTaxStatusOptions } from '../constants';
import {
  ALL_ENTITY_CATEGORIES_QUERY,
  CREATE_INVESTOR_MUTATION,
  CREATE_LEGAL_ENTITY_MUTATION,
  EDIT_INVESTOR_PAGE_LIST_INVESTOR_HOUSEHOLDS_QUERY,
  UPDATE_LEGAL_ENTITY_MUTATION
} from '../queries';
import {
  EditInvestorFormDevider,
  EditInvestorFormFieldsWrapper,
  EditInvestorFormFooter,
  EditInvestorModalPageForm,
  EditInvestorModalPageWrapper
} from '../styles';
import {
  EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum,
  EditInvestorEntityDetailsFormMode,
  EditInvestorEntityDetailsTaxStatusOptionEnum,
  IEditInvestorFormData
} from '../types';

const eligibilityOptions: (keyof typeof Eligibility)[] = Object.keys(Eligibility) as (keyof typeof Eligibility)[];

interface EditEntityDetailsModalPageProps {
  formMode: EditInvestorEntityDetailsFormMode;
  entityDetailsFormData: IEditInvestorFormData['entityDetails'] | null;
  familyTenantId: string | null;
  refetchEntityOrGroupData: () => Promise<void>;
  investorLegalEntitiesRefetch: () => Promise<ApolloQueryResult<InvestorDetailsGetInvestorLegalEntitiesQuery>>;
  handleCloseModalPage: ({ updateSearchParams }: { updateSearchParams: boolean }) => void;
}

export const EditEntityDetailsModalPage = ({
  formMode,
  entityDetailsFormData,
  familyTenantId,
  refetchEntityOrGroupData,
  investorLegalEntitiesRefetch,
  handleCloseModalPage
}: EditEntityDetailsModalPageProps) => {
  const goBack = useGoBack();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { searchParams } = useSearchParamsManager();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { t } = useTranslation(['editInvestorPage', 'enums', 'common']);

  const {
    control,
    formState: { isSubmitting, dirtyFields },
    reset,
    setValue,
    watch,
    handleSubmit
  } = useForm<IEditInvestorFormData['entityDetails']>();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const isCreateMode = formMode === EditInvestorEntityDetailsFormMode.Create;
  const isUpdateMode = formMode === EditInvestorEntityDetailsFormMode.Update;
  const isStartingFromInvestorListPage = location?.pathname?.includes('create-investor');
  const isPlatformManagerUser = user && user?.tenant?.type === TenantType.PLATFORM_MANAGER;

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [advisoryFirmList, setAdvisoryFirmList] = useState<ISelectValue[]>([]);
  const [investorHouseholdList, setInvestorHouseholdList] = useState<ISelectValue[]>([]);
  const [categorySelectOptions, setCategorySelectOptions] = useState<ISelectValue[]>([]);
  const [entityTypeSelectOptions, setEntityTypeSelectOptions] = useState<ISelectValue[]>([]);
  const [showOtherTypeInput, setShowOtherTypeInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createInvestor] = useMutation(CREATE_INVESTOR_MUTATION);
  const [createLegalEntity] = useMutation(CREATE_LEGAL_ENTITY_MUTATION);
  const [updateLegalEntity] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION);

  useEffect(() => {
    if (isCreateMode) {
      reset({
        name: '',
        ...(investorId && { familyId: investorId }),
        ...(!isPlatformManagerUser && user?.tenant?.id && { tenantId: user?.tenant?.id }),
        ...(isPlatformManagerUser && familyTenantId && { tenantId: familyTenantId }),
        taxStatus: EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAXABLE,
        investorHouseholdOption: EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.CreateNewInvestorHousehold
      });
      return;
    }
    if (!entityDetailsFormData) return;
    reset(entityDetailsFormData);
    setShowOtherTypeInput(entityDetailsFormData?.entityType === getEnumKeyByValue(EntityType, EntityType.Other));
  }, [entityDetailsFormData, user]);

  const { loading: advisoryFirmListLoading } = useQuery(ALL_ADVISORY_FIRM_LIST_QUERY, {
    skip: !isStartingFromInvestorListPage || !isPlatformManagerUser || !isCreateMode,
    onCompleted: ({ allAdvisoryFirmList }) => {
      const advisoryFirmListSelectOptions = [
        ...new Set(allAdvisoryFirmList.map((advisoryFirm) => createSimpleSelectOption(advisoryFirm.id, advisoryFirm.name)))
      ];
      setAdvisoryFirmList(advisoryFirmListSelectOptions);
    }
  });

  const { loading: investorHouseholdListLoading } = useQuery(EDIT_INVESTOR_PAGE_LIST_INVESTOR_HOUSEHOLDS_QUERY, {
    skip: !isStartingFromInvestorListPage || !isCreateMode || !watch('tenantId'),
    variables: {
      data: {
        sortBy: InvestorSortBy.Investor,
        sortDirection: InvestorSortDirection.Asc,
        search: null,
        limit: 1000,
        offset: 0,
        includeRecommendations: true,
        filter: advisoryFirmList.find((advisoryFirm) => advisoryFirm.value === watch('tenantId'))?.label
      }
    },
    onCompleted: (data) => {
      const { listInvestors } = data;
      const investorHouseholdListSelectOptions = [
        ...new Set(listInvestors.data.map((investor) => createSimpleSelectOption(investor.id, investor.name)))
      ];
      setInvestorHouseholdList(investorHouseholdListSelectOptions);
    }
  });

  const createEntityTypeSelectOptions = (entityTypes: (keyof typeof EntityType)[]) => {
    return entityTypes.map((entityType) => createSimpleSelectOption(entityType, t(`enums:EntityType.${entityType}`)));
  };

  const createCategorySelectOptions = (categories: (keyof typeof Category)[]) => {
    return categories.map((category) => createSimpleSelectOption(category, t(`enums:Category.${category}`)));
  };

  const { data: entityCategoriesData, loading: entityCategoriesLoading } = useQuery(ALL_ENTITY_CATEGORIES_QUERY, {
    onCompleted: (data) => {
      const categoryOptions = createCategorySelectOptions(data?.entityCategories?.map((categoryItem) => categoryItem.category) ?? []);
      setCategorySelectOptions(categoryOptions ?? []);
      if (entityDetailsFormData?.category) {
        const selectedCategory = data?.entityCategories.find((option) => option.category === entityDetailsFormData.category);
        setEntityTypeSelectOptions(createEntityTypeSelectOptions(selectedCategory?.entityTypes ?? []));
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const handleCloseEditEntityDetailsModalPage = () => {
    if (isCreateMode && isStartingFromInvestorListPage) {
      goBack({ fallBack: '/investors' });
      return;
    }

    handleCloseModalPage({ updateSearchParams: true });
  };

  const handleModalWindow = ({ type }: { type: string }) => {
    setModalWindow({ type, isOpen: !!type });
  };

  const handleNewInvestorHousehold = (investorHouseholdName: string) => {
    setValue('familyId', 'new-investor-household');
    setValue('newHouseholdName', investorHouseholdName);
    const newInvestorHouseholdOption = createSimpleSelectOption('new-investor-household', investorHouseholdName);
    const filteredInvestorHouseholdList = investorHouseholdList.filter((investor) => investor.value !== 'new-investor-household');
    setInvestorHouseholdList([newInvestorHouseholdOption, ...filteredInvestorHouseholdList]);
  };

  const onSubmit = async (data: IEditInvestorFormData['entityDetails']) => {
    try {
      setErrorMessage(null);

      if (isCreateMode) {
        const { name, taxStatus, familyId, tenantId, investorHouseholdOption, newHouseholdName } = data;
        const taxStatusEnumKey: TaxStatus = getEnumKeyByValue(EditInvestorEntityDetailsTaxStatusOptionEnum, taxStatus) as TaxStatus;
        const isAssignToExisting = investorHouseholdOption === EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.AssignToExisting;
        const tenantName = advisoryFirmList.find((advisoryFirm) => advisoryFirm.value === tenantId)?.label || '';
        let newFamilyId: string | null = null;

        if ((isAssignToExisting && familyId === 'new-investor-household' && newHouseholdName) || (!isAssignToExisting && newHouseholdName)) {
          const { data: createInvestorData } = await createInvestor({
            variables: {
              data: {
                tenantName,
                name: newHouseholdName
              }
            }
          });

          newFamilyId = createInvestorData?.createInvestor?.id || null;
        }

        const { data: createLegalEntityData } = await createLegalEntity({
          variables: {
            data: {
              tenantId,
              entityName: name,
              taxStatus: taxStatusEnumKey,
              familyId: newFamilyId || familyId
            }
          }
        });

        const { id, family } = createLegalEntityData?.createLegalEntity || {};
        const backToTitle = searchParams.get('backToTitle');
        const isGroup = searchParams.get('isGroup');

        const queryParams = new URLSearchParams({
          ...(isGroup && { isGroup })
        });

        navigate(`/investors/${family?.id}/${id}?${queryParams.toString()}&backToTitle=${backToTitle}`, { replace: true });
        navigate(`/investors/${family?.id}/${id}/edit?${queryParams.toString()}&backToTitle=Investor Details`);
        if (!isStartingFromInvestorListPage) {
          await investorLegalEntitiesRefetch();
        }
        handleCloseModalPage({ updateSearchParams: false });
        return;
      }
      if (isUpdateMode) {
        const { name, taxStatus, category, entityType, otherType, eligibility, aboutInvestorNote, entityInception } = data;
        const {
          name: isDirtyName,
          taxStatus: isDirtyTaxStatus,
          category: isDirtyCategory,
          entityType: isDirtyEntityType,
          eligibility: isDirtyEligibility,
          aboutInvestorNote: isDirtyAboutInvestorNote,
          entityInception: isDirtyEntityInception
        } = dirtyFields;
        const taxStatusEnumKey: TaxStatus = getEnumKeyByValue(EditInvestorEntityDetailsTaxStatusOptionEnum, taxStatus) as TaxStatus;

        await updateLegalEntity({
          variables: {
            data: {
              id: entityId,
              ...(isDirtyName && { entityName: name }),
              ...(isDirtyTaxStatus && { taxStatus: taxStatusEnumKey }),
              ...(isDirtyCategory && { category: category as gqlCategory }),
              ...(isDirtyEntityType && { entityType: entityType as gqlEntityType }),
              ...(isDirtyEntityType && { otherType: entityType === getEnumKeyByValue(EntityType, EntityType.Other) ? otherType : null }),
              ...(isDirtyEligibility && { eligibility: eligibility as gqlEligibility[] }),
              ...(isDirtyAboutInvestorNote && { aboutInvestorNote: aboutInvestorNote || null })
              // ...(isDirtyEntityInception && { entityInception: entityInception || null })
            }
          }
        });

        if (entityDetailsFormData?.name !== name) {
          await investorLegalEntitiesRefetch();
        }
        await refetchEntityOrGroupData();
        handleCloseModalPage({ updateSearchParams: true });
        return;
      }
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'add-household' && (
        <AddInvestorHousehold
          isOpen={modalWindow.isOpen}
          handleNewInvestorHousehold={handleNewInvestorHousehold}
          onClose={() => handleModalWindow({ type: '' })}
        />
      )}
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseEditEntityDetailsModalPage} backToTitle="Edit Investor" />} />
        <PageTitle title={isCreateMode ? 'Add New Investor Entity' : 'Entity Details'} />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <EditInvestorModalPageForm onSubmit={handleSubmit(onSubmit)}>
            <EditInvestorFormFieldsWrapper>
              {isStartingFromInvestorListPage && isPlatformManagerUser && (
                <TwoColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="tenantId"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t('editEntityDetailsModalPage.form.tenantId.errorMessage')
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        value={value || ''}
                        setValue={onChange}
                        options={advisoryFirmList}
                        errorText={error?.message}
                        label={t('editEntityDetailsModalPage.form.tenantId.label')}
                        isRequiredField
                        styleType="form"
                        size="md"
                        loading={advisoryFirmListLoading}
                      />
                    )}
                  />
                  <Banner
                    icon={<InfoIcon fill={themeVar('menu.tab')} />}
                    title=""
                    description={t('editEntityDetailsModalPage.advisoryFirmInfoBanner.description')}
                    color={themeVar('font.base')}
                    bgColor={themeVar('layer.base')}
                    dividerColor={themeVar('border.base')}
                    withBorderRadius
                  />
                </TwoColumnWrapper>
              )}
              <AnimatePresence>
                {(!isCreateMode || !isStartingFromInvestorListPage || watch('tenantId')) && (
                  <motion.div key="entityDetails" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <EntityDetailsWrapper>
                      <TwoColumnWrapper isMobile={isMobile}>
                        <Controller
                          name="name"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t('editEntityDetailsModalPage.form.name.errorMessage')
                            }
                          }}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                              value={value || ''}
                              onChange={(e) => {
                                const entityName = e.target.value;
                                onChange(entityName);
                                if (!entityName) {
                                  setValue('newHouseholdName', '');
                                }
                              }}
                              errorText={error?.message}
                              label={t('editEntityDetailsModalPage.form.name.label')}
                              isRequiredField
                              styleType="form"
                              size="md"
                              tooltipWidth={400}
                              tooltip={t('editEntityDetailsModalPage.form.name.tooltip')}
                            />
                          )}
                        />
                      </TwoColumnWrapper>
                      {isStartingFromInvestorListPage && (
                        <InvestorHouseholdWrapper isMobile={isMobile}>
                          <div>
                            <Label size="md">{t('editEntityDetailsModalPage.form.investorHouseholdOption.label')}</Label>
                            <Controller
                              name="investorHouseholdOption"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <div>
                                  {editInvestorAddInvestorEntityInvestorHouseholdOptions.map((option, index) => (
                                    <RadioButton
                                      key={option + index}
                                      name="investorHouseholdOption"
                                      radioButtonText={option}
                                      selectedOption={value}
                                      handleRadioChange={(event) => onChange(event.target.value)}
                                    />
                                  ))}
                                </div>
                              )}
                            />
                          </div>
                          <AnimatePresence mode="wait">
                            {watch('investorHouseholdOption') ===
                              EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.CreateNewInvestorHousehold && (
                              <motion.div key="investorHouseholdInput" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                <Controller
                                  name="newHouseholdName"
                                  control={control}
                                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Input
                                      value={value || ''}
                                      onChange={onChange}
                                      errorText={error?.message}
                                      label={t('editEntityDetailsModalPage.form.newHouseholdName.label')}
                                      placeholder={watch('name') || ''}
                                      onClick={() => {
                                        const entityName = watch('name');
                                        const newHouseholdName = watch('newHouseholdName');
                                        if (!newHouseholdName) {
                                          onChange(entityName || '');
                                        }
                                      }}
                                      styleType="form"
                                      size="md"
                                      tooltip={t('editEntityDetailsModalPage.form.newHouseholdName.tooltip')}
                                    />
                                  )}
                                />
                              </motion.div>
                            )}
                            {watch('investorHouseholdOption') === EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.AssignToExisting && (
                              <motion.div key="investorHouseholdSelect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                <Controller
                                  name="familyId"
                                  control={control}
                                  rules={{
                                    required: {
                                      value: true,
                                      message: t('editEntityDetailsModalPage.form.familyId.errorMessage')
                                    }
                                  }}
                                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Select
                                      value={value || ''}
                                      setValue={(value) => {
                                        onChange(value);
                                        if (watch('newHouseholdName')) {
                                          setValue('newHouseholdName', null);
                                        }
                                      }}
                                      options={investorHouseholdList}
                                      errorText={error?.message}
                                      label={t('editEntityDetailsModalPage.form.familyId.label')}
                                      isRequiredField
                                      withSearch
                                      styleType="form"
                                      size="md"
                                      loading={investorHouseholdListLoading}
                                      additionalOption={
                                        <AddNewInvestorHouseholdButton
                                          size="sm"
                                          type="button"
                                          styleType="outline"
                                          onClick={() => handleModalWindow({ type: 'add-household' })}
                                        >
                                          Add New Household
                                        </AddNewInvestorHouseholdButton>
                                      }
                                    />
                                  )}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </InvestorHouseholdWrapper>
                      )}
                      <RadioButtonWithNotification
                        control={control}
                        name="taxStatus"
                        label={t('editEntityDetailsModalPage.form.taxStatus.label')}
                        options={editInvestorEntityDetailsTaxStatusOptions}
                      />
                      {!isCreateMode && (
                        <>
                          <TwoColumnWrapper isMobile={isMobile}>
                            <Controller
                              name="category"
                              control={control}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <>
                                  <Select
                                    value={value}
                                    setValue={(value) => {
                                      onChange(value);
                                      if (value !== watch('category')) {
                                        setValue('entityType', null);
                                      }
                                      const selectedCategory = entityCategoriesData.entityCategories.find((option) => option.category === value);
                                      setEntityTypeSelectOptions(createEntityTypeSelectOptions(selectedCategory?.entityTypes ?? []));
                                    }}
                                    options={categorySelectOptions}
                                    loading={entityCategoriesLoading}
                                    errorText={error?.message}
                                    label={t('editEntityDetailsModalPage.form.category.label')}
                                    size="md"
                                  />
                                </>
                              )}
                            />
                            <Controller
                              name="entityType"
                              control={control}
                              rules={{
                                validate: (value) => {
                                  if (watch('category') && !value) {
                                    return t('editEntityDetailsModalPage.form.entityType.errorMessage');
                                  }
                                  return true;
                                }
                              }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <>
                                  <Select
                                    value={value}
                                    setValue={(value) => {
                                      onChange(value);
                                      setShowOtherTypeInput(watch('entityType') === getEnumKeyByValue(EntityType, EntityType.Other));
                                    }}
                                    options={entityTypeSelectOptions}
                                    loading={entityCategoriesLoading}
                                    errorText={error?.message}
                                    label={t('editEntityDetailsModalPage.form.entityType.label')}
                                    styleType={!watch('category') ? 'disabled' : 'default'}
                                    isRequiredField={!!watch('category')}
                                    size="md"
                                  />
                                </>
                              )}
                            />
                          </TwoColumnWrapper>
                          {showOtherTypeInput && (
                            <TwoColumnWrapper isMobile={isMobile}>
                              <Controller
                                name="otherType"
                                control={control}
                                rules={{
                                  required: {
                                    value: true,
                                    message: t('editEntityDetailsModalPage.form.otherType.errorMessage')
                                  }
                                }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                  <Input
                                    label={t('editEntityDetailsModalPage.form.otherType.label')}
                                    value={value || ''}
                                    onChange={(e) => {
                                      onChange(e.target.value);
                                    }}
                                    errorText={error?.message}
                                    size="md"
                                    isRequiredField
                                  />
                                )}
                              />
                            </TwoColumnWrapper>
                          )}
                          <EligibilityWrapper isMobile={isMobile}>
                            <div>
                              <Controller
                                name="eligibility"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <>
                                    <Label size="md">{t('editEntityDetailsModalPage.form.eligibility.label')}</Label>
                                    {eligibilityOptions.map((item) => (
                                      <CheckboxWrapper
                                        key={item}
                                        id={item}
                                        onClick={() => {
                                          const updatedEligibility = value?.includes(item) ? value.filter((el) => el !== item) : [...value, item];
                                          onChange(updatedEligibility);
                                        }}
                                      >
                                        <CheckBox id={item} isActive={value?.includes(item)} />
                                        {t(`enums:Eligibility.${item}`)}
                                      </CheckboxWrapper>
                                    ))}
                                  </>
                                )}
                              />
                            </div>
                            <div>
                              <Banner
                                title=""
                                description={t('editEntityDetailsModalPage.eligibilityInfoBanner.description')}
                                icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
                                bgColor={themeVar('layer.base')}
                                color={themeVar('font.base')}
                              />
                            </div>
                          </EligibilityWrapper>
                          <Controller
                            name="aboutInvestorNote"
                            control={control}
                            rules={{
                              validate: (value) => value?.length <= 300 || 'Maximum number of characters exceeded'
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <Textarea
                                label={t('editEntityDetailsModalPage.form.aboutInvestorNote.label')}
                                value={value || ''}
                                onChange={(e) => {
                                  onChange(e.target.value);
                                }}
                                size="md"
                                errorText={error?.message}
                                limitCharactersTo={300}
                              />
                            )}
                          />
                          <TwoColumnWrapper isMobile={isMobile}>
                            <Controller
                              name="entityInception"
                              control={control}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DatePicker
                                  label={t('editEntityDetailsModalPage.form.entityInception.label')}
                                  value={value || ''}
                                  onChange={onChange}
                                  size="md"
                                  errorText={error?.message}
                                />
                              )}
                            />
                          </TwoColumnWrapper>
                        </>
                      )}
                    </EntityDetailsWrapper>
                  </motion.div>
                )}
              </AnimatePresence>
              <EditInvestorFormDevider />
            </EditInvestorFormFieldsWrapper>
            <EditInvestorFormFooter>
              <Button styleType="outline" onClick={handleCloseEditEntityDetailsModalPage} type="button" size="lg">
                {t('common:cancel')}
              </Button>
              <Button styleType={isSubmitting ? 'disabled' : 'default'} type="submit" size="lg">
                {t('common:save')}
              </Button>
            </EditInvestorFormFooter>
          </EditInvestorModalPageForm>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const EntityDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const InvestorHouseholdWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
`;

const AddNewInvestorHouseholdButton = styled(Button)`
  display: flex;
  align-self: flex-end;
  margin-left: auto;
`;

const EligibilityWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
`;

const CheckboxWrapper = styled.div`
  cursor: pointer;
  display: flex;
  gap: 9px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 10px;
`;
